import { useState, useEffect, useRef } from 'react';
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameMonth, parseISO, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';
import { useSettings } from '../functions/useSettings';
import { getDateLocale } from '../functions/getDateLocale';

function buildMonthGrid(month: Date): Date[] {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days: Date[] = [];
  let cursor = start;
  while (cursor <= end) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
}

export function formatDateKeyboardInput(raw: string) {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
}

export function isValidDateString(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = parseISO(value);
  return !Number.isNaN(parsed.getTime());
}

export function ThemedDateField({
  label,
  value,
  onChange,
  placeholder,
  allowClear = false,
  maxDate,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  /** ISO date string (yyyy-MM-dd) — days after this date are disabled */
  maxDate?: string;
}) {
  const { language } = useLanguage();
  const { dateFormat: _dateFormat } = useSettings();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const locale = getDateLocale(language);
  const [month, setMonth] = useState<Date>(() => {
    if (value) {
      try { return startOfMonth(parseISO(value)); } catch { /* noop */ }
    }
    return startOfMonth(new Date());
  });

  useEffect(() => {
    if (!value) return;
    try {
      setMonth(startOfMonth(parseISO(value)));
    } catch {
      /* noop */
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const weekdays = Array.from({ length: 7 }, (_, index) =>
    format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), index), 'EEE', { locale })
  );
  const days = buildMonthGrid(month);

  return (
    <div className="relative" ref={popoverRef}>
      <label className="block text-xs text-text-secondary mb-1">{label}</label>
      <div className="flex items-center rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-highlight-dark transition-colors">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(event) => onChange(formatDateKeyboardInput(event.target.value))}
          placeholder={placeholder ?? 'YYYY-MM-DD'}
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-text-primary outline-none placeholder:text-text-secondary/50"
        />
        <button
          type="button"
          onClick={() => setOpen(current => !current)}
          className="mr-1.5 flex h-8 w-8 items-center justify-center rounded-md text-text-secondary hover:bg-gray-100"
          aria-label={`Open ${label} picker`}
        >
          <CalendarDays size={16} />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-[280px] rounded-2xl border border-gray-200 bg-white shadow-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setMonth(prev => subMonths(prev, 1))}
              className="p-1.5 rounded-full text-text-secondary hover:bg-gray-100"
            >
              <ChevronLeft size={14} />
            </button>
            <p className="text-sm font-bold text-text-primary">
              {format(month, 'MMMM yyyy', { locale })}
            </p>
            <button
              type="button"
              onClick={() => setMonth(prev => addMonths(prev, 1))}
              className="p-1.5 rounded-full text-text-secondary hover:bg-gray-100"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {weekdays.map(day => (
              <div key={day} className="text-center text-[10px] font-bold uppercase tracking-wide py-1 text-text-secondary/60">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map(day => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const isCurrentMonth = isSameMonth(day, month);
              const isSelected = value === dateKey;
              const isPastMax = maxDate ? dateKey > maxDate : false;
              return (
                <button
                  key={dateKey}
                  type="button"
                  disabled={!isCurrentMonth || isPastMax}
                  onClick={() => {
                    onChange(dateKey);
                    setOpen(false);
                  }}
                  className={cn(
                    'h-8 rounded-lg text-xs font-semibold transition-colors',
                    (!isCurrentMonth || isPastMax) && 'opacity-30 cursor-default',
                    isSelected
                      ? 'bg-primary-brown text-white'
                      : 'text-text-primary hover:bg-gray-100'
                  )}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {allowClear && value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-text-secondary hover:bg-gray-50"
            >
              Clear date
            </button>
          )}
        </div>
      )}
    </div>
  );
}
